import React, { useEffect, useState } from "react";
import { Button, Modal, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import { isAppSession, useAuthSession } from "../../lib/http/session";
import { noteActivity, refreshSession, sessionKeeperSnapshot } from "../../lib/http/refresh";
import { signOut } from "../../utils/auth/sessionUtilities";

const { Paragraph } = Typography;

export const IDLE_WARNING_MS = 120_000;
// Just past the deadline a refresh lets identity's 401 end the session, not this tab's clock.
const EXPIRY_CHECK_DELAY_MS = 5_000;

const countdown = (ms: number): string => {
	const seconds = Math.max(0, Math.ceil(ms / 1000));
	return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

// Mounted once inside the router: warns two minutes before the staff idle limit signs the session out (D5).
const IdleSignOutHost: React.FC = () => {
	const navigate = useNavigate();
	const auth = useAuthSession();
	const times = isAppSession(auth) ? auth.info?.session : undefined;
	const idleAt = Date.parse(times?.idle_expires_at ?? "");
	// Capped at the absolute limit no activity can move the deadline, so the notice says the session is ending instead of offering to stay.
	const ending = idleAt >= Date.parse(times?.absolute_expires_at ?? "");
	const [phase, setPhase] = useState<"waiting" | "open" | "dismissed">("waiting");
	const [now, setNow] = useState(Date.now);

	useEffect(() => {
		if (!Number.isFinite(idleAt)) {
			setPhase("waiting");
			return;
		}
		const delay = idleAt - IDLE_WARNING_MS - Date.now();
		if (delay > 0) setPhase("waiting");
		const timer = setTimeout(() => {
			// Input not yet reported (here or in another tab) keeps the session alive without asking.
			if (!ending && sessionKeeperSnapshot().hadInput) void refreshSession();
			else setPhase("open");
		}, Math.max(0, delay));
		return () => clearTimeout(timer);
	}, [idleAt, ending]);

	useEffect(() => {
		if (phase !== "open") return;
		setNow(Date.now());
		const tick = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(tick);
	}, [phase]);

	useEffect(() => {
		if (phase === "waiting") return;
		const check = setTimeout(() => void refreshSession(), Math.max(0, idleAt + EXPIRY_CHECK_DELAY_MS - Date.now()));
		return () => clearTimeout(check);
	}, [phase, idleAt]);

	const stay = () => {
		noteActivity();
		void refreshSession();
	};

	const leave = () => {
		void signOut().finally(() => navigate("/login", { replace: true }));
	};

	return (
		<Modal
			title={ending ? "Your session is ending" : "Are you still there?"}
			open={phase === "open"}
			closable={false}
			maskClosable={false}
			keyboard={false}
			destroyOnClose
			width={420}
			footer={[
				<Button key="signout" data-testid="session-idle-signout" onClick={leave}>
					Sign out
				</Button>,
				ending ? (
					<Button key="dismiss" type="primary" data-testid="session-idle-dismiss" onClick={() => setPhase("dismissed")}>
						OK
					</Button>
				) : (
					<Button key="stay" type="primary" data-testid="session-idle-stay" onClick={stay}>
						Stay signed in
					</Button>
				),
			]}
		>
			<div data-testid="session-idle-warning">
				<Paragraph>
					{ending ? (
						<>
							For your security your session ends in <strong>{countdown(idleAt - now)}</strong>. Save your work; you will need to sign in again.
						</>
					) : (
						<>
							For your security you will be signed out in <strong>{countdown(idleAt - now)}</strong> because there has been no activity.
						</>
					)}
				</Paragraph>
			</div>
		</Modal>
	);
};

export default IdleSignOutHost;
